<script>
import CertPills from '@components/cert-pills.vue'
import GradeList from '@components/booking/grade-list.vue'
import BookingListPolicies from '@components/booking/booking-list-policies.vue'
import BookingListFeedback from '@components/booking/booking-list-feedback.vue'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import formatDateMixin from '@/mixins/formatDateMixin'
import baseMixin from '@/mixins/baseMixin'
import { BookingCommentType } from '@/shared/constants/booking/BookingCommentType'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'
import { mapGetters } from 'vuex'
import BookingCardMenu from './booking-card-menu.vue'
import BookingCardIdLabel from './booking-card-id-label.vue'

export default {
  name: 'BookingCard',
  components: {
    CertPills,
    BookingListPolicies,
    GradeList,
    BookingListFeedback,
    CandidateImage,
    BookingCardMenu,
    BookingCardIdLabel,
  },
  mixins: [baseMixin, formatDateMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      profileImgSrc: null,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
    }),
    howLate() {
      if (!this.booking.runningLate) return 0

      const items = this.booking.noteList.filter(
        (item) => item.type === BookingCommentType.LATE
      )
      if (items.length === 0) return 0

      const jsonObj = JSON.parse(items[0].text)
      return jsonObj.LateMins
    },
    hasGrades() {
      return this.booking.grades.length > 0
    },
    hasCandidate() {
      return this.booking.candidate && this.booking.candidate.id > 0
    },
    compRunningLateClass() {
      return !this.booking.runningLate ? '' : 'red--text font-weight-medium'
    },
    compDuration() {
      return `${this.renderTime24(
        this.booking.startDateTimeLocal
      )} - ${this.renderTime24(this.booking.endDateTimeLocal)}`
    },
    compCandidateFullName() {
      if (!this.hasCandidate) return ''
      return `${this.booking.candidate.firstName} ${this.booking.candidate.lastName}`
    },
    compFilledCandidateId() {
      return this.hasCandidate ? this.booking.candidate.id : -1
    },
    compFilledCandidateFirstName() {
      return !this.hasCandidate ? null : this.booking.candidate.firstName
    },
    compFilledCandidateLastName() {
      return !this.hasCandidate ? null : this.booking.candidate.lastName
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    bookingIsPendingApproval() {
      return this.booking.status === ClientBookingStatus.pendingApproval
    },
  },
  methods: {
    viewCandidate() {
      if (this.hasCandidate) {
        const candidateInput = {
          id: this.booking.candidate.id,
          fullName:
            this.booking.candidate.firstName +
            ' ' +
            this.booking.candidate.lastName,
          profilePicUrl: this.profileImgSrc,
        }
        this.$emit('viewCandidate', candidateInput)
      }
    },
    updateProfileImage(data) {
      this.profileImgSrc = data
    },
    showCancelBookingDialog() {
      this.$emit('cancelBooking', this.booking)
    },
  },
}
</script>

<template>
  <v-card class="mb-5 booking-card custom-elevation rounded-xl" elevation="0">
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6" lg="5" xl="4" class="d-flex">
          <div>
            <v-lazy>
              <CandidateImage
                :id="compFilledCandidateId"
                :first-name="compFilledCandidateFirstName"
                :last-name="compFilledCandidateLastName"
                rounded
                size="xxxl"
                clickable
                @profileImgSrc="updateProfileImage"
                @click="viewCandidate"
              />
            </v-lazy>
          </div>

          <div class="flex-grow-1">
            <BookingCardIdLabel :booking="booking" />

            <h5 class="font-weight-medium subtitle-1">
              <a
                v-if="hasCandidate"
                title="View Profile"
                @click="viewCandidate"
              >
                {{ compCandidateFullName }}
              </a>
              <span v-else>
                {{ this.$t('booking.unfilledBookingText') }}
              </span>
            </h5>

            <p
              :class="[
                'subtitle-1 font-weight-medium mb-0',
                compRunningLateClass,
              ]"
            >
              {{ compDuration }}
              <span
                v-if="booking.runningLate"
                class="booking-card__running-late"
              >
                +{{ $t('booking.minsLateText', { count: howLate }) }}
              </span>
              <span
                title="Classification"
                class="booking-card__classification caption my-0"
              >
                {{ booking.type }}
              </span>
            </p>

            <div v-if="booking.replacing" class="booking-card__replacing">
              {{ $t('booking.replacingLabel') }}:
              <span class="d-inline-block font-weight-medium">
                {{
                  `${booking.replacing.firstName} ${booking.replacing.lastName}`
                }}
              </span>
            </div>

            <GradeList v-if="hasGrades" :grades="booking.grades" />

            <div v-if="hasCandidate" class="badges mb-2">
              <CertPills :certifications="booking.certChecks" />
              <BookingListPolicies :policies="booking.agreements" />
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" lg="5" xl="6">
          <div class="booking-card__details">
            <div v-if="booking.roomNumber" class="booking-card__room">
              {{ $t('booking.roomLabel') }}:
              <span class="font-weight-medium"> {{ booking.roomNumber }} </span>
            </div>

            <div v-if="booking.notes" class="booking-card__notes">
              {{ $t('booking.notesLabel') }}:
              <span>
                {{ booking.notes }}
              </span>
            </div>
          </div>
        </v-col>

        <v-col class="d-flex align-center justify-end" cols="12" md="12" lg="2">
          <div
            v-if="canCreateBookings && !bookingIsPendingApproval"
            class="text-right d-flex flex-row-reverse align-center"
          >
            <BookingCardMenu
              @showCancelBookingDialog="showCancelBookingDialog"
            />
            <BookingListFeedback
              v-if="hasCandidate"
              :booking-id="booking.id"
              :candidate="booking.candidate"
              :feedback="booking.feedback"
            />
          </div>
        </v-col>
      </v-row>

      <div class="clearfix"> </div>
    </v-card-text>
  </v-card>
</template>
